import React from "react";
import { Helmet } from "react-helmet";
import TechIntro from "../ReusableComponents/TechIntro";
import TechWhyChoose from "../ReusableComponents/TechWhyChoose";
import TechRoles from "../ReusableComponents/TechRoles";
import TechStaffingServices from "../ReusableComponents/TechStaffingServices";
import TechRecruitingAgency from "../ReusableComponents/TechRecruitingAgency";
import TechOutcomes from "../ReusableComponents/TechOutcomes";
import TechCTA from "../ReusableComponents/TechCTA";

const roleGroups = [
  {
    title: 'Security Operations & Engineering',
    items: [
      { name: 'SOC Analyst (L1/L2/L3)', icon: '🛡️' },
      { name: 'Security Engineer (SIEM/SOAR)', icon: '⚙️' },
      { name: 'Incident Response Analyst', icon: '🚨' },
      { name: 'Threat Intelligence Analyst', icon: '🔍' },
    ]
  },
  {
    title: 'Offensive & Cloud Security',
    items: [
      { name: 'Penetration Tester', icon: '💻' },
      { name: 'Red Team Operator', icon: '⚔️' },
      { name: 'Cloud Security Architect', icon: '☁️' },
      { name: 'AppSec Engineer', icon: '🔒' },
    ]
  },
  {
    title: 'Strategy, GRC & Access',
    items: [
      { name: 'CISO / vCISO', icon: '👔' },
      { name: 'GRC Analyst', icon: '📋' },
      { name: 'IAM / PAM Engineer', icon: '🔑' },
      { name: 'Cyber Ark', icon: '🕵️' },
    ]
  }
];

const features = [
  {
    icon: '✦',
    title: 'Domain-Specific Security Expertise',
    text: 'Our recruiters understand certifications like CISSP, CEH, CISM, OSCP, and CCSP — and the real-world skills behind them. We assess candidates on actual security competencies, not just credentials.',
  },
  {
    icon: '✔',
    title: 'Thorough Security Vetting',
    text: 'All consultants are assessed for: technical security skills and tool proficiency, relevant certifications (CISSP, CEH, CISM, OSCP, CompTIA Security+), hands-on experience in your specific security domain, and background check readiness.',
  },
  {
    icon: '⚙',
    title: 'Flexible Engagement Models',
    text: 'Staff augmentation, project-based consulting, or permanent placement. From an individual threat analyst to a fully embedded security operations team — we scale to your needs.',
  },
  {
    icon: '🛡',
    title: 'Compliance & Framework Alignment',
    text: 'We match candidates with proven experience in your required frameworks — NIST CSF, ISO 27001, MITRE ATT&CK, Zero Trust, SOC 2, CMMC, and more.',
  },
];

const problemItems = [
  'Undetected threats and breaches from understaffed or under-skilled security operations centers.',
  'Compliance failures in frameworks like NIST, ISO 27001, SOC 2, HIPAA, and PCI-DSS.',
  'Delayed incident response due to teams stretched thin or lacking specialized expertise.',
  'Reputational and financial damage from data breaches that qualified talent could have prevented.',
  'Audit failures and regulatory fines from gaps in security posture and documentation.',
];

const solutionPoints = [
  'Recruiters who understand SOC analysts, red team operators, and cloud security architects',
  'Certified talent matched to your specific threat profile and compliance requirements',
  'We deliver verified, battle-tested security professionals, not generic IT resumes',
];

const outcomes = [
  {
    title: 'Stronger Security Posture',
    text: 'Qualified professionals who reduce your attack surface and harden defenses from day one.',
  },
  {
    title: 'Faster Incident Response',
    text: 'Experienced analysts who can detect, contain, and remediate threats at speed.',
  },
  {
    title: 'Continuous Compliance',
    text: 'Certified experts who keep your organization audit-ready across all required frameworks.',
  },
  {
    title: 'Reduced Breach Risk',
    text: 'Vetted professionals who understand your threat landscape and industry-specific risks.',
  },
  {
    title: 'Scalable Security Capacity',
    text: 'Access a ready bench of cybersecurity talent that grows with your threat environment.',
  },
];

const CyberSecurityStaffHome = () => {
  return (
    <div>
      <Helmet>
        <title>Cybersecurity Staffing India | Certified Cyber & InfoSec Recruitment | Kapil Technologies</title>
        <meta
          name="description"
          content="Kapil Technologies provides specialized cybersecurity staffing solutions. Hire pre-vetted SOC analysts, penetration testers, GRC consultants, cloud security engineers, and vCISOs."
        />
        <meta
          name="keywords"
          content="cybersecurity staffing India, cybersecurity recruitment, hire infoSec consultants, SOC analyst staffing, penetration testers India, GRC consultants, IAM engineers, cloud security architect staffing, vulnerability management analyst, red team operator recruitment, Kapil Technologies cybersecurity"
        />
        <meta name="author" content="Kapil Technologies" />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Cybersecurity Staffing India | Certified Cyber & InfoSec Recruitment | Kapil Technologies"
        />
        <meta
          property="og:description"
          content="Kapil Technologies provides specialized cybersecurity staffing solutions. Hire pre-vetted SOC analysts, penetration testers, GRC consultants, cloud security engineers, and vCISOs."
        />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Cybersecurity Staffing India | Certified Cyber & InfoSec Recruitment"
        />
        <meta
          name="twitter:description"
          content="Certified cybersecurity staffing solutions. Hire pre-vetted SOC analysts, penetration testers, GRC consultants, cloud security engineers, and vCISOs."
        />
        <link
          rel="canonical"
          href="https://kapiltechnologies.com/staff-augmentation/cybersecurity"
        />
      </Helmet>

      {/* Section 1: Hero */}
      <TechIntro 
        videoUrl="https://res.cloudinary.com/djaejg3nn/video/upload/v1781602787/Kapil_Tech_Cybersecurity_Staffing_Page_ml0uss.mp4"
        titleWhite="Certified InfoSec & Cyber Talent."
        titleGradient="Pre-Vetted. Security-Cleared Ready."
      />

      {/* Section 2: Why Choose */}
      <TechWhyChoose 
        title="Struggling to Find Qualified Cybersecurity Professionals?"
        description="Kapil Tech connects organizations with battle-tested cybersecurity experts — from SOC analysts and penetration testers to CISO-level leaders and cloud security architects. In a threat landscape that never sleeps, you can't afford to wait weeks for the right hire."
        stats={[
          { value: "250+", label: "Cyber Experts Placed" },
          { value: "97%", label: "Client Satisfaction Rate" }
        ]}
        imageUrl="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Technology/Kapil_Tech_Cybersecurity_Staffing.jpg?updatedAt=1781603013955"
        imageAlt="Cybersecurity Staffing by Kapil Tech"
      />

      {/* Section 3: Roles & Specializations */}
      <TechRoles 
        title="Cybersecurity Roles & Specializations We Staff"
        description="Our cybersecurity talent network covers every layer of your security posture."
        roleGroups={roleGroups}
      />

      {/* Section 4: Why Enterprises Choose Us */}
      <TechStaffingServices 
        title="Why Organizations Choose Kapil Tech for Cybersecurity Staffing"
        description="We combine deep cybersecurity domain expertise with an agile staffing model to deliver top-tier talent at speed."
        features={features}
      />

      {/* Section 5: Recruiting Agency / Problem-Solution */}
      <TechRecruitingAgency 
        title="Is the Cybersecurity Talent Gap Leaving Your Organization Exposed?"
        description="The global cybersecurity workforce shortage has reached critical levels. With threat actors growing more sophisticated and compliance mandates expanding, organizations face a dangerous gap between the security talent they need and what generalist staffing agencies can deliver."
        problemTitle="The Real Cost of a Cybersecurity Staffing Gap"
        problemItems={problemItems}
        solutionTitle="Kapil Tech: Your Dedicated Cybersecurity Staffing Partner"
        solutionDescription="Kapil Tech maintains a focused cybersecurity staffing practice with recruiters who understand the nuances between a SOC Tier 2 analyst, a red team operator, and a cloud security architect. We don't send you generic IT resumes — we deliver verified security professionals."
        solutionPoints={solutionPoints}
        ctaText="Find Your Cybersecurity Talent Today"
      />

      {/* Section 6: Business Outcomes */}
      <TechOutcomes 
        title="Business Outcomes You Can Expect"
        description="Partner with Kapil Tech to accelerate your cybersecurity initiatives and achieve measurable risk reduction."
        outcomes={outcomes}
      />

      {/* Section 7: CTA */}
      <TechCTA 
        title="Ready to Hire Top"
        titleGradient="Cybersecurity Experts?"
        description="Share your security staffing requirements today. Our team responds within hours with pre-vetted, certified cybersecurity professionals."
      />
    </div>
  );
};

export default CyberSecurityStaffHome;
